/**
 * LandingScaledPreview — Base canvas at fixed size, then uniform scale to fit container.
 * UI is never squashed: it renders at baseW x baseH, then we apply transform: scale() only.
 * Uses ResizeObserver to measure container; scale = min(containerW/baseW, containerH/baseH).
 */

import * as React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { useResizeObserver } from "@/components/landing/hooks/useResizeObserver";
import { cn } from "@/lib/utils";

const pillTransition = { duration: 0.22, ease: [0, 0, 0.2, 1] as const };

export type CanvasCallout = {
  /** X in canvas px (0..baseW) */
  x: number;
  /** Y in canvas px (0..baseH) */
  y: number;
  label: string;
  /** Line end X in canvas px (optional) */
  toX?: number;
  /** Line end Y in canvas px (optional) */
  toY?: number;
};

type LandingScaledPreviewProps = {
  baseW: number;
  baseH: number;
  children: React.ReactNode;
  /** Callouts in canvas px; overlay positions computed from scale/offset */
  callouts?: CanvasCallout[];
  className?: string;
};

export function LandingScaledPreview({
  baseW,
  baseH,
  children,
  callouts = [],
  className,
}: LandingScaledPreviewProps) {
  const reduce = useReducedMotion();
  const [containerRef, size] = useResizeObserver<HTMLDivElement>();

  const scale =
    size.width > 0 && size.height > 0
      ? Math.min(size.width / baseW, size.height / baseH)
      : 0;
  const offsetX = size.width > 0 ? (size.width - baseW * scale) / 2 : 0;
  const offsetY = size.height > 0 ? (size.height - baseH * scale) / 2 : 0;

  return (
    <div
      ref={containerRef}
      className={cn("relative h-full w-full overflow-hidden", className)}
      style={{ minHeight: 0 }}
    >
      <div
        className="pointer-events-none absolute left-0 top-0 overflow-visible"
        style={{
          width: baseW,
          height: baseH,
          transformOrigin: "top left",
          transform:
            scale > 0
              ? `translate(${offsetX}px, ${offsetY}px) scale(${scale})`
              : undefined,
        }}
      >
        <div
          className="h-full w-full overflow-hidden rounded-lg"
          style={{ width: baseW, height: baseH }}
        >
          {children}
        </div>
      </div>

      {/* Callouts: positions in container % from canvas px * scale + offset */}
      {callouts.length > 0 && size.width > 0 && size.height > 0 && scale > 0 && (
        <div
          className="pointer-events-none absolute inset-0 z-10"
          aria-hidden
        >
          {callouts.map((c, i) => {
            const leftPct = ((offsetX + c.x * scale) / size.width) * 100;
            const topPct = ((offsetY + c.y * scale) / size.height) * 100;
            return (
              <motion.div
                key={i}
                className="absolute -translate-x-1/2 -translate-y-1/2"
                style={{ left: `${leftPct}%`, top: `${topPct}%` }}
                initial={reduce ? false : { opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ ...pillTransition, delay: i * 0.06 }}
              >
                <span
                  className={cn(
                    "inline-flex rounded-full px-2.5 py-1.5 text-xs font-medium backdrop-blur-sm",
                    "bg-black/40 dark:bg-white/15 border border-white/10 text-white shadow-sm"
                  )}
                >
                  {c.label}
                </span>
              </motion.div>
            );
          })}
          {/* Leader lines in container space */}
          <svg
            className="absolute inset-0 h-full w-full"
            style={{ overflow: "visible" }}
          >
            <defs>
              <marker
                id="scaled-callout-arrow"
                markerWidth="5"
                markerHeight="5"
                refX="4"
                refY="2.5"
                orient="auto"
              >
                <path d="M0,0 L5,2.5 L0,5 Z" fill="white" opacity={0.7} />
              </marker>
            </defs>
            {callouts.map(
              (c, i) =>
                c.toX != null &&
                c.toY != null && (
                  <line
                    key={i}
                    x1={offsetX + c.toX * scale}
                    y1={offsetY + c.toY * scale}
                    x2={offsetX + c.x * scale}
                    y2={offsetY + c.y * scale}
                    stroke="rgba(255,255,255,0.4)"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    markerEnd="url(#scaled-callout-arrow)"
                  />
                )
            )}
          </svg>
        </div>
      )}
    </div>
  );
}
